import {type Moment} from 'moment';
import React, {useState} from 'react';
import {createFragmentContainer, graphql} from 'react-relay';
import {TimeRegistrationTargetSuggstionsData} from '../../../time-registration-target-suggestions/TimeRegistrationTargetSuggestionsFetch';
import {TIME_PERIOD, type TimePeriod} from '../../../timesheets_change_date_view';
import {TimesheetsTimeRowTable as TimesheetTable} from '../TimesheetsTimeRowTable';
import {ApprovalStatus} from '../../../../../../constants';
import type {TimesheetColumnSort} from '../../usePersistentTimesheetColumnSort';
import {groupRegs, addTimeRegTypes, sortList} from '../timesheet-table-util/DataUtil';
import {TimesheetInternalTimeTableWrapper_viewer} from './__generated__/TimesheetInternalTimeTableWrapper_viewer.graphql';
import {partition} from 'lodash';
import {useTimesheetsInternalTimeTableColumns} from './useTimesheetsInternalTimeTableColumns';

type InternalTimeCategories = NonNullable<
	NonNullable<NonNullable<TimesheetInternalTimeTableWrapper_viewer['company']>['internalTimes']>['edges']
>;
type InternalTimeCategoryNode = NonNullable<NonNullable<InternalTimeCategories[0]>['node']>;

function flattenInternalTimeCategories(internalTimeCategories: InternalTimeCategories): InternalTimeCategoryNode[] {
	return internalTimeCategories.reduce((acc: InternalTimeCategoryNode[], category) => {
		if (category && category.node) {
			acc.push(category.node);
		}
		return acc;
	}, []);
}

function partitionQueryInternalTimeWithTimeRegs(
	queryTasksFlat: InternalTimeCategoryNode[],
	timeRegsForPeriod
): [InternalTimeCategoryNode[], InternalTimeCategoryNode[]] {
	const timeRegistrationIdleTimeIdsSet = new Set(
		timeRegsForPeriod.filter(tReg => !!tReg?.node?.idleTime).map(tReg => tReg.node.idleTime.id)
	);
	return partition(queryTasksFlat, idleTime => timeRegistrationIdleTimeIdsSet.has(idleTime.id));
}

type TimesheetInternalTimeTableWrapperProps = {
	viewer: TimesheetInternalTimeTableWrapper_viewer;
	selectedPerson; // TimesheetsPage_viewer['company']['person']
	timeRegistrations; // TimesheetsPage_viewer['timeRegistrations']['edges']
	timeRegsForPeriod; // TimesheetsPage_viewer['timeRegistrations']['edges']
	currentViewingDate: Moment;
	timePeriod: TimePeriod;
	sort: TimesheetColumnSort;
	selectDate: (day: Moment) => void;
	holidays; // TimesheetsPage_viewer['company']['person']['holidayCalendar']['holidayCalendarEntries']['edges']
	lockedDate?: Moment;
	isFullyLocked: boolean;
	usingTimeApproval: boolean;
	usingInternalTimeApproval: boolean;
	periodTimeApprovalStatus: ApprovalStatus | null;
	overBudgetProgramsByCompanyProjectId; // Custom type of {companyProjectId, program}
	timeRegistrationTargetSuggestionsData: TimeRegistrationTargetSuggstionsData;
	goToTimesheetsTab: () => void;
	showSuggestions: boolean;
	hasSuggestions: boolean;
	refetchEntries: () => void;
};

const TimesheetInternalTimeTableWrapper = ({
	viewer,
	selectedPerson,
	timeRegistrations,
	timeRegsForPeriod,
	currentViewingDate,
	timePeriod,
	sort,
	selectDate,
	holidays,
	lockedDate,
	isFullyLocked,
	usingTimeApproval,
	usingInternalTimeApproval,
	periodTimeApprovalStatus,
	overBudgetProgramsByCompanyProjectId,
	timeRegistrationTargetSuggestionsData,
	goToTimesheetsTab,
	showSuggestions,
	hasSuggestions,
	refetchEntries,
}: TimesheetInternalTimeTableWrapperProps) => {
	const isWeekView = timePeriod === TIME_PERIOD.WEEK_VIEW;

	const columns = useTimesheetsInternalTimeTableColumns({isWeekView});

	const internalTimeCategories = flattenInternalTimeCategories(viewer.company?.internalTimes?.edges ?? []);

	const [queryTimeCategoriesWithTimeRegistrations, queryTimeCategoriesWithoutTimeRegistrations] =
		partitionQueryInternalTimeWithTimeRegs(internalTimeCategories, timeRegsForPeriod);

	const [convertedInternalTime, setConvertedInternalTime] = useState([]);

	const queryInternalTimeWithTimeRegistrationsIds = queryTimeCategoriesWithTimeRegistrations
		.map(task => task.id)
		.concat(convertedInternalTime);

	const timeRegsMoved = timeRegsForPeriod.filter(
		tReg =>
			tReg.node.idleTime &&
			queryInternalTimeWithTimeRegistrationsIds.some(idleTimeId => idleTimeId === tReg.node.idleTime.id)
	);

	const timeRegsWithTypes = addTimeRegTypes(timeRegsMoved);

	// TODO: In an effort to standardise the timesheet table we are in the process of refactoring
	// it to take a single list of row entities, rather than an array or map, of edges or nodes.
	// It's likely that much of this complexity be be refactored away.
	let standardisedEntityList;
	if (isWeekView) {
		const groupedMovedInternalTime = [...groupRegs(timeRegsWithTypes).entries()];

		standardisedEntityList = [
			...groupedMovedInternalTime.map(([entity, dayRegsForEntity]) => ({
				...entity,
				switchIdleTime: true,
				dayRegs: dayRegsForEntity,
			})),
			...queryTimeCategoriesWithoutTimeRegistrations.map(category => ({
				...category,
				switchIdleTime: true,
				dayRegs: [[], [], [], [], [], [], []],
			})),
		];
	} else {
		const combinedTaskList = timeRegsWithTypes.concat(queryTimeCategoriesWithoutTimeRegistrations);

		standardisedEntityList = combinedTaskList
			.filter(e => !(e.node && e.node.task && e.node.task.project === null))
			.map(e => ({...e, switchIdleTime: true}));
	}

	const sortedList = sortList(standardisedEntityList, sort.value, sort.ascending);

	return (
		<TimesheetTable
			entityList={sortedList}
			sort={sort}
			setExternalMovedIdleTimes={setConvertedInternalTime}
			loading={false}
			viewer={viewer}
			selectedPerson={selectedPerson}
			columns={columns}
			timeRegistrations={timeRegistrations}
			currentViewingDate={currentViewingDate}
			timePeriod={timePeriod}
			searchActive={false}
			selectDate={selectDate}
			holidays={holidays}
			lockedDate={lockedDate}
			isFullyLocked={isFullyLocked}
			usingTimeApproval={usingTimeApproval}
			usingInternalTimeApproval={usingInternalTimeApproval}
			periodTimeApprovalStatus={periodTimeApprovalStatus}
			overBudgetProgramsByCompanyProjectId={overBudgetProgramsByCompanyProjectId}
			timeRegistrationTargetSuggestionsData={timeRegistrationTargetSuggestionsData}
			goToTimesheetsTab={goToTimesheetsTab}
			thisWeekWork={false}
			showSuggestions={showSuggestions}
			hasSuggestions={hasSuggestions}
			refetchEntries={refetchEntries}
		/>
	);
};

const TimesheetInternalTimeTableWrapperQuery = graphql`
	query TimesheetInternalTimeTableWrapper_Query {
		viewer {
			actualPersonId
			component(name: "TimesheetInternalTimeTableWrapper")
			...TimesheetInternalTimeTableWrapper_viewer
		}
	}
`;

export {TimesheetInternalTimeTableWrapperQuery};

export default createFragmentContainer(TimesheetInternalTimeTableWrapper, {
	viewer: graphql`
		fragment TimesheetInternalTimeTableWrapper_viewer on Viewer {
			id
			actualPersonId
			harvestUser
			unit4User
			company {
				id
				bambooHREnabled
				hiBobEnabled
				harvestEnabled
				unit4Enabled
				sageIntacctEnabled
				useTimeOffApproval
				roles(includeDisabled: false) {
					edges {
						node {
							id
							name
						}
					}
				}
				internalTimes: idleTimes(first: 100000, internalTime: true, excludeDisabled: true) {
					edges {
						node {
							id
							name
							favoured
							isInternalTime
						}
					}
				}
			}
		}
	`,
});
